import { Box } from '@chakra-ui/react/box';
import { Portal } from '@chakra-ui/react/portal';
import { Tooltip, type TooltipRootProps } from '@chakra-ui/react/tooltip';
import { isValidElement, type ReactNode, type Ref, useContext, useRef } from 'react';

import { type BitkitIconComponent } from '../../icons';
import BitkitPortalContext from '../../utilities/BitkitPortalContext';

export type BitkitTooltipProps = {
  /**
   * The trigger the tooltip is anchored to. Rendered via `asChild`, so it must accept a ref —
   * unless `wrapTrigger` puts a wrapper in between.
   */
  children: ReactNode;
  /** Dismiss the tooltip when the trigger is clicked. */
  closeOnClick?: TooltipRootProps['closeOnClick'];
  /**
   * Render the tooltip inline instead of in a portal. Only needed when it must stay inside its
   * parent's DOM subtree — a dialog already sets this for you.
   */
  disablePortal?: boolean;
  /** Render the trigger without a tooltip — for hints that only apply in some states. */
  disabled?: TooltipRootProps['disabled'];
  /**
   * Icon rendered after `text`, at size 16. Use it for the affordance the tooltip describes — an
   * `IconArrowNortheast` when the trigger opens something in a new tab, for instance.
   */
  icon?: BitkitIconComponent;
  /** Called when the tooltip opens or closes, including from hover and focus. */
  onOpenChange?: TooltipRootProps['onOpenChange'];
  /** Controlled open state. Leave unset to let hover and focus drive it. */
  open?: TooltipRootProps['open'];
  /** Delay in ms before an uncontrolled tooltip opens on hover. */
  openDelay?: TooltipRootProps['openDelay'];
  /** Where the tooltip sits relative to the trigger (default `top`). It flips if space is short. */
  placement?: NonNullable<TooltipRootProps['positioning']>['placement'];
  /** The tooltip's text. Plain string only — use `icon` for a glyph after it. */
  text: string;
  /**
   * Render the trigger inside a focusable wrapper instead of on the trigger itself. Only for triggers
   * that cannot take focus — on a focusable one this adds a second, redundant tab stop for the same
   * control. A disabled `<button>` dispatches no pointer events and is out of the tab order, so
   * without a wrapper the tooltip never opens — in the one state whose explanation the user actually
   * needs.
   *
   * Chakra v2's `Tooltip` had this built in as `shouldWrapChildren`, rendering a
   * `<span display="inline-block" tabIndex={0}>` that carried the trigger props. Ark's tooltip has no
   * equivalent, and Chakra's own v2->v3 codemod replaces it with a bare `<span>` — which restores
   * hover but silently drops the tab stop, so the tooltip stays unreachable by keyboard. This prop is
   * that escape hatch, `tabIndex` included.
   *
   * The wrapper is a plain focusable span, so focusing it announces the trigger's label and the
   * tooltip text, not the trigger's own state — a disabled button inside is not re-announced as
   * disabled. Conveying that would mean keeping the trigger focusable with `aria-disabled` instead
   * of the native attribute, which is a change to what `disabled` means, not a tooltip concern.
   */
  wrapTrigger?: boolean;
};

/**
 * A hover/focus tooltip anchored to its child: `text` carries the message and `icon` appends a
 * glyph after it. Text only — a tooltip closes when focus leaves its trigger and ignores touch, so a
 * link or a button inside one couldn't be reached. For those use `BitkitToggletip`, which looks the
 * same and opens on click.
 *
 * The content renders in a portal, so it escapes any ancestor that traps stacking (`isolation`,
 * `transform`, a `z-index`ed positioned box) or clips overflow. Dialogs turn that off through
 * `BitkitPortalContext`; `disablePortal` is the manual override.
 */
const BitkitTooltip = ({
  children,
  closeOnClick,
  disablePortal,
  disabled,
  icon: Icon,
  onOpenChange,
  open,
  openDelay,
  placement = 'top',
  text,
  wrapTrigger,
}: BitkitTooltipProps) => {
  const { disablePortal: disablePortalFromContext } = useContext(BitkitPortalContext);

  // Anchor positioning to the trigger node by ref rather than relying on Zag's
  // id/data-scope lookup. When the trigger is also wrapped by another `asChild`
  // trigger (e.g. Menu.Trigger), the outer trigger's id and data-scope/data-part
  // overwrite ours on the shared node, so `getTriggerEl()` can't find it and the
  // tooltip loses its anchor. `getAnchorElement` takes precedence over the id
  // lookup, so this is safe in the standalone case too.
  const triggerRef = useRef<HTMLElement>(null);

  return (
    <Tooltip.Root
      closeOnClick={closeOnClick}
      disabled={disabled}
      onOpenChange={onOpenChange}
      open={open}
      openDelay={openDelay}
      positioning={{ getAnchorElement: () => triggerRef.current, placement, offset: { mainAxis: 4, crossAxis: 0 } }}
    >
      <Tooltip.Trigger ref={triggerRef as Ref<HTMLButtonElement>} asChild>
        {wrapTrigger || !isValidElement(children) ? (
          // inline-flex so the wrapper shrink-wraps the trigger: a plain inline span adds descender
          // space below it and shifts the alignment of whatever sits next to it.
          <Box
            as="span"
            display="inline-flex"
            tabIndex={0}
            css={{
              // The wrapper owns the tab stop, but the focus ring belongs on the trigger: an outline
              // follows the border-radius of the element it is drawn on, and the wrapper has none —
              // so leaving it here squares off a pill or a round trigger.
              _focusVisible: {
                outline: 'none',
                '& > *': {
                  outlineColor: 'var(--focus-ring-color)',
                  outlineOffset: 'var(--focus-ring-offset, 2px)',
                  outlineStyle: 'var(--focus-ring-style, solid)',
                  outlineWidth: 'var(--focus-ring-width, 2px)',
                },
              },
            }}
          >
            {children}
          </Box>
        ) : (
          children
        )}
      </Tooltip.Trigger>
      <Portal disabled={disablePortal || disablePortalFromContext}>
        <Tooltip.Positioner>
          <Tooltip.Content>
            <Tooltip.Arrow>
              <Tooltip.ArrowTip />
            </Tooltip.Arrow>
            <Box display="flex" gap="8">
              {text}
              {Icon && <Icon flexShrink="0" size="16" />}
            </Box>
          </Tooltip.Content>
        </Tooltip.Positioner>
      </Portal>
    </Tooltip.Root>
  );
};

BitkitTooltip.displayName = 'BitkitTooltip';

export default BitkitTooltip;
